import React, {useState} from 'react';
import {DialogOverlay, DialogContent} from '@reach/dialog';
import {animated, useTransition} from 'react-spring';
import {useFormState} from 'react-use-form-state';
import {compose, graphql} from 'react-apollo';
import gql from 'graphql-tag';

import {Button, EqualColumns, Input, Loading} from 'ui-kit';

import * as styles from './NewStaffMemberModal.module.css';

const AnimatedDialogOverlay = animated(DialogOverlay);
const AnimatedDialogContent = animated(DialogContent);

function NewStaffMemberModal({show, mutate, onDismiss, onSubmit}) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formState, {text}] = useFormState({
    email: '',
    firstName: '',
    lastName: '',
  });
  const transitions = useTransition(show, null, {
    from: {opacity: 0, y: -20},
    enter: {opacity: 1, y: 0},
    leave: {opacity: 0, y: -20},
    config: {duration: 100},
  });

  async function handleSubmit(event) {
    event.preventDefault();

    setLoading(true);
    setErrors({});

    try {
      const {data} = await mutate({
        variables: {
          input: {
            email: formState.values.email,
            firstName: formState.values.firstName,
            lastName: formState.values.lastName,
          },
        },
      });
      const {
        createStaffMember: {errors},
      } = data;

      if (errors.length > 0) {
        const clientErrors = errors.reduce((allErrors, {path, message}) => {
          if (allErrors[path] == null) {
            return {
              ...allErrors,
              [path]: message,
            };
          }

          return {
            ...allErrors,
            [path]: `${allErrors[path]}; ${message}`,
          };
        }, {});

        setErrors(clientErrors);
        setLoading(false);
      } else {
        setLoading(false);
        onSubmit();
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  return transitions.map(({item: show, key, props: {opacity, y}}) => (
    <AnimatedDialogOverlay
      key={key}
      style={{opacity}}
      onDismiss={onDismiss}
      isOpen={show}
    >
      <AnimatedDialogContent
        className={styles.Dialog}
        style={{
          transform: y.interpolate(y => `translate3d(0px, ${y}px, 0px)`),
        }}
      >
        <h2 className={styles.Heading}>New staff member</h2>

        <form method="post" onSubmit={handleSubmit}>
          <EqualColumns>
            <Input
              label="First name"
              noPadding
              {...text('firstName')}
              errorMessage={errors.firstName}
            />
            <Input
              label="Last name"
              noPadding
              {...text('lastName')}
              errorMessage={errors.lastName}
            />
          </EqualColumns>

          <EqualColumns columns={1}>
            <Input
              label="Email"
              noPadding
              {...text('email')}
              errorMessage={errors.email}
            />
          </EqualColumns>

          <p className={styles.DisclaimerText}>
            <small>
              An email will be sent to this email address with an invitation for
              this staff member to finish setting up their account.
            </small>
          </p>

          <div className={styles.ButtonGroup}>
            <Button disabled={loading}>
              {loading ? <Loading immediate /> : 'Submit'}
            </Button>
            <Button
              disabled={loading}
              color="light"
              onClick={onDismiss}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </AnimatedDialogContent>
    </AnimatedDialogOverlay>
  ));
}

export default compose(
  graphql(
    gql`
      mutation NewStaffMember($input: CreateStaffMemberInput!) {
        createStaffMember(input: $input) {
          errors {
            path
            message
          }
          staffMember {
            id
            firstName
            lastName
            email
          }
        }
      }
    `,
  ),
)(NewStaffMemberModal);
